import { Step1FormProps, WizzardStep1Form } from '../types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback } from 'react'
import { DefaultValue } from '../constants'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import { VALIDATIONPATTERN_EMAIL, VALIDATIONPATTERN_PHONE, VALIDATION_REQUIRED } from '../../../../constants/validation'

const useStep1Form = (activationObject?: ActivationObject): Step1FormProps => {
    const propName = (name: keyof WizzardStep1Form): string => name

    const validationSchema = React.useMemo(
        () =>
            yup.object().shape({
                [propName('personName')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('personType')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('personAuthorisationType')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('anotherAuthorisationType')]: yup.string().when('personAuthorisationType', {
                    is: 'Other',
                    then: yup.string().required(VALIDATION_REQUIRED),
                }),
                [propName('personPhoneNumber')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .matches(VALIDATIONPATTERN_PHONE, 'Špatný formát tel. čísla (zadávejte mobilní tel. číslo)'),
                [propName('personEmail')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .email('Špatný formát emailové adresy')
                    .matches(VALIDATIONPATTERN_EMAIL, 'Špatný formát emailové adresy'),
                [propName('useSettingsAnotherContract')]: yup.boolean(),
                [propName('numberAnotherContract')]: yup.string().when('useSettingsAnotherContract', {
                    is: true,
                    then: yup.string().required(VALIDATION_REQUIRED),
                }),
                [propName('uploadedFile')]: yup.boolean().when('personAuthorisationType', {
                    is: 'DeputyManagingDirector',
                    then: yup
                        .boolean()
                        .required('Je nutné nahrát dokument o plné moci.')
                        .oneOf([true], 'Je nutné nahrát dokument o plné moci.'),
                }),

                [propName('check1')]: yup
                    .boolean()
                    .required('')
                    .oneOf([true], 'Zaškrtněte prosím seznámení se zpracováním osobních údajů'),
                [propName('check2')]: yup
                    .boolean()
                    .required('')
                    .oneOf([true], 'Zaškrtněte prosím seznámení se všeobecnými obchodními podmínkami'),
                [propName('objectType')]: yup.string().notRequired().nullable(),
            }),
        []
    )

    const form = useForm<WizzardStep1Form>({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: DefaultValue,
    })

    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                personName: activationObject?.personName ?? DefaultValue.personName,
                personType: activationObject?.personType ?? DefaultValue.personType,
                personAuthorisationType: activationObject?.personAuthorisationType ?? DefaultValue.personAuthorisationType,
                personPhoneNumber: activationObject?.personPhoneNumber ?? DefaultValue.personPhoneNumber,
                personEmail: activationObject?.personEmail ?? DefaultValue.personEmail,
                useSettingsAnotherContract:
                    activationObject.useSettingsAnotherContract ?? DefaultValue.useSettingsAnotherContract,
                numberAnotherContract: activationObject?.numberAnotherContract ?? DefaultValue.numberAnotherContract,
                uploadedFile: activationObject?.uploadedFile ?? DefaultValue.uploadedFile,
                unifyInvoicing: activationObject?.unifyInvoicing ?? DefaultValue.unifyInvoicing,
                anotherAuthorisationType: activationObject?.anotherAuthorisationType ?? DefaultValue.anotherAuthorisationType,
                check1: false,
                check2: false,
                objectType: activationObject?.objectType,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep1Form) => {
            if (activationObject) {
                const newObject: ActivationObject = { ...activationObject, ...data, wizardStep: 'ObjectIdentification' }
                update(newObject)
            }
        },
        [activationObject, update]
    )

    return {
        form,
        onSubmit,
    }
}

export default useStep1Form
